// Feature 159: DTO della chat AI (AgentApi, specs/159-chat-ai/contracts). Le date sono UTC.

export interface AiChatModello {
  id: string;
  nome: string;
  default?: boolean;
}

export interface AiChatConsumi {
  oggiOrganizzazione: number;
  settimanaPersonale: number;
  mesePersonale: number;
  azzeramentoUtc: string;
}

export interface AiChatStato {
  modello: AiChatModello;
  quotaGiornaliera: number;
  consumi: AiChatConsumi;
  quotaEsaurita: boolean;
}

export interface AiChatConversazione {
  id: string;
  titolo: string;
  /** Feature 164: conversazione per scrivere un contesto; assente nelle chat normali. */
  scopo?: AiChatScopo | null;
  messaggi: number;
  totaleToken: number;
  createdAtUtc: string;
  updatedAtUtc: string;
}

export interface AiChatParametro {
  nome: string;
  valore: string;
}

export type AiChatEsitoComando = 'ok' | 'errore';

export interface AiChatComando {
  id: string;
  nome: string;
  etichetta: string;
  parametri: AiChatParametro[];
  esito: AiChatEsitoComando;
  durataMs: number;
  righe?: number;
  messaggio?: string;
}

// Feature 160: grafici calcolati da AgentApi dal risultato dei comandi e link verificati alle entità.

export type AiChatTipoGrafico = 'barre' | 'barreOrizzontali' | 'linea' | 'ciambella' | 'kpi' | 'avanzamento';

export interface AiChatSerie {
  nome: string;
  valori: (number | null)[];
}

export interface AiChatGrafico {
  id: string;
  tipo: AiChatTipoGrafico;
  titolo: string;
  etichette: string[];
  serie: AiChatSerie[];
  impilate?: boolean;
  unita?: string | null;
  nota?: string | null;
  fonte: string;
}

export type AiChatTipoEntita = 'ordine' | 'articolo' | 'evento' | 'movimento' | 'contenitore' | 'risorsa' | 'spedizione';

export interface AiChatCollegamento {
  marcatore: string;
  tipo: AiChatTipoEntita;
  chiavi: string[];
}

export type AiChatEsitoMessaggio = 'ok' | 'errore' | 'interrotto';

export interface AiChatMessaggio {
  id: number;
  ruolo: 'user' | 'assistant';
  contenuto: string;
  esito?: AiChatEsitoMessaggio;
  inputTokens?: number;
  outputTokens?: number;
  createdAtUtc: string;
  comandi?: AiChatComando[];
  grafici?: AiChatGrafico[];
  collegamenti?: AiChatCollegamento[];
  contestoProposto?: AiChatContestoProposta | null;
}

export interface AiChatConversazioneDettaglio {
  id: string;
  titolo: string;
  scopo?: AiChatScopo | null;
  totaleToken: number;
  messaggi: AiChatMessaggio[];
}

export interface AiChatConsumiOrg {
  oggi: number;
  ultimi7Giorni: number;
  mese: number;
  giorniQuotaEsaurita7: number;
}

export interface AiChatConsumoOperatore {
  operatoreId: number;
  conversazioni: number;
  tokenMese: number;
  ultimoUsoUtc: string;
}

export interface AiChatImpostazioni {
  modelloId: string | null;
  modelloEffettivo: AiChatModello;
  quotaGiornaliera: number | null;
  quotaEffettiva: number;
  consumi: AiChatConsumiOrg;
  operatori: AiChatConsumoOperatore[];
}

export interface AiChatImpostazioniRequest {
  modelloId: string | null;
  quotaGiornaliera: number | null;
}

export interface AiChatDone {
  messaggioId: number;
  titolo: string;
  inputTokens: number;
  outputTokens: number;
  totaleChat: number;
  oggiOrganizzazione: number;
  quotaEsaurita: boolean;
  collegamenti?: AiChatCollegamento[];
}

/** Evento SSE della risposta: `type` è il nome della riga `event:` (contracts/sse-eventi.md). */
export type AiChatEvento =
  | { type: 'status'; stato: 'pensando' | 'rispondendo' }
  | { type: 'chunk'; testo: string }
  | { type: 'tool_start'; id: string; nome: string; etichetta: string; parametri: AiChatParametro[] }
  | { type: 'tool_end'; id: string; esito: AiChatEsitoComando; durataMs: number; righe?: number; messaggio?: string }
  | { type: 'usage'; inputTokens: number; outputTokens: number }
  | ({ type: 'grafico' } & AiChatGrafico)
  | ({ type: 'contesto' } & AiChatContestoProposta)
  | ({ type: 'done' } & AiChatDone)
  | { type: 'error'; codice: string; messaggio: string };

/** Errore restituito prima dello stream o dalle API REST: `codice` solo se il server lo manda. */
export interface AiChatErrore {
  status: number;
  codice?: string;
  messaggio: string;
  azzeramentoUtc?: string;
}

// Feature 164: contesti della chat (specs/164-contesti-chat-ai/contracts/api-contesti.md).

export type AiChatAmbitoContesto = 'generale' | 'organizzazione';

export type AiChatScopo = 'contestoGenerale' | 'contestoOrganizzazione';

export type AiChatOrigineContesto = 'manuale' | 'assistente' | 'ripristino';

export interface AiChatContesto {
  ambito: AiChatAmbitoContesto;
  testo: string;
  attivo: boolean;
  versione: number;
  caratteri: number;
  maxCaratteri: number;
  tokenStimati: number;
  modificabile: boolean;
  predefinito: boolean;
  predefinitoAggiornato: boolean;
  updatedByOperatoreId?: number | null;
  updatedAtUtc?: string | null;
}

export interface AiChatContesti {
  generale: AiChatContesto;
  organizzazione: AiChatContesto;
}

export interface AiChatContestoVersione {
  versione: number;
  attivo: boolean;
  origine: AiChatOrigineContesto;
  createdByOperatoreId: number;
  createdAtUtc: string;
  caratteri: number;
  testo?: string | null;
}

export interface AiChatSalvaContesto {
  testo: string;
  attivo: boolean;
  versioneLetta: number;
  origine?: 'manuale' | 'assistente';
}

/** Proposta del tool `proponi_contesto`: evento SSE `contesto` e `contestoProposto` dei messaggi salvati. */
export interface AiChatContestoProposta {
  ambito: AiChatAmbitoContesto;
  organizzazioneId: number;
  testo: string;
  sintesi: string;
  caratteri: number;
}

export function scopoDi(ambito: AiChatAmbitoContesto): AiChatScopo {
  return ambito === 'generale' ? 'contestoGenerale' : 'contestoOrganizzazione';
}
